/**
 * IntegrationService — base class every INT-2 through INT-7 connector extends.
 *
 * Responsibilities:
 *   • Persist + read `connectors`, `connector_sync_logs`, `connector_field_maps`
 *     through DataService (never touches Firestore directly).
 *   • Provide a registry so connector classes can declare their connectorType
 *     and setup-component name.
 *   • Provide a uniform mock-mode guard so missing credentials render as
 *     "connected (mock)" rather than errored.
 *   • Provide health-check + sync hooks that subclasses override.
 */

import DataService from './DataService';
import { DEMO_TENANT_ID, normalizeTenantId } from '../data/tenantDefaults';

const CONNECTORS_COLLECTION = 'connectors';
const SYNC_LOGS_COLLECTION = 'connector_sync_logs';
const FIELD_MAPS_COLLECTION = 'connector_field_maps';

/**
 * SET-006 — Audit metadata schema (all fields nullable/optional; additive only).
 *
 * Firestore connector document shape (v3, additive):
 *   lastAuditedAt:        Timestamp | null   — ISO string when stored via DataService
 *   lastAuditedBy:        string | null       — userId of last auditor
 *   credentialExpiresAt:  Timestamp | null   — ISO string of credential/token expiry
 *   auditStatus:          'verified' | 'expiring_soon' | 'expired' | 'unverified'
 *
 * computeAuditStatus derives the enum from credentialExpiresAt:
 *   — null → 'unverified'
 *   — expired → 'expired'
 *   — within 30 days → 'expiring_soon'
 *   — otherwise → 'verified'
 */

const EXPIRING_SOON_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Compute auditStatus from a credentialExpiresAt ISO string (or null).
 * Pure function — safe to call in both service and UI layers.
 * @param {string|null} credentialExpiresAt
 * @returns {'verified'|'expiring_soon'|'expired'|'unverified'}
 */
export function computeAuditStatus(credentialExpiresAt) {
  if (!credentialExpiresAt) return 'unverified';
  const expiresMs = new Date(credentialExpiresAt).getTime();
  const nowMs = Date.now();
  if (expiresMs <= nowMs) return 'expired';
  if (expiresMs - nowMs <= EXPIRING_SOON_MS) return 'expiring_soon';
  return 'verified';
}

const registry = new Map();

export function isMockMode(envKey) {
  if (typeof window === 'undefined') return true;
  const raw = import.meta?.env?.[envKey];
  if (!raw) return true;
  if (raw === 'pending' || raw === 'mock') return true;
  return false;
}

function tid(tenantId) {
  return normalizeTenantId(tenantId || DEMO_TENANT_ID);
}

const IntegrationService = {
  // ── Registry ───────────────────────────────────────────────────────────

  register(connectorType, definition) {
    registry.set(connectorType, {
      connectorType,
      label: definition.label,
      category: definition.category || 'generic',
      description: definition.description || '',
      icon: definition.icon || '🔌',
      envKey: definition.envKey || null,
      setupComponent: definition.setupComponent || null,
      defaultInterval: definition.defaultInterval || 15,
      directions: definition.directions || ['inbound'],
      capabilities: definition.capabilities || [],
      // Hook: subclasses pass connect/disconnect/sync/healthCheck implementations.
      hooks: {
        connect: definition.connect || null,
        disconnect: definition.disconnect || null,
        sync: definition.sync || null,
        healthCheck: definition.healthCheck || null,
        // Returns a source-field schema for the field-mapping UI.
        describeSchema: definition.describeSchema || null,
      },
    });
  },

  listRegistered() {
    return Array.from(registry.values());
  },

  getDefinition(connectorType) {
    return registry.get(connectorType) || null;
  },

  // ── Connectors CRUD ────────────────────────────────────────────────────

  async listConnectors(tenantId) {
    return DataService.query(CONNECTORS_COLLECTION, {}, tid(tenantId));
  },

  async getConnector(connectorId, tenantId) {
    return DataService.getById(CONNECTORS_COLLECTION, connectorId, tid(tenantId));
  },

  async createConnector(data, userId = 'system') {
    const def = registry.get(data.connectorType) || null;
    const credentialExpiresAt = data.credentialExpiresAt ?? null;
    const payload = {
      ...data,
      tenantId: tid(data.tenantId),
      status: data.status || 'pending',
      health: data.health || 'yellow',
      syncIntervalMinutes: data.syncIntervalMinutes || def?.defaultInterval || 15,
      mockMode: data.mockMode ?? (def?.envKey ? isMockMode(def.envKey) : true),
      lastSyncAt: null,
      lastHealthCheckAt: null,
      lastError: null,
      config: data.config || {},
      // SET-006: audit metadata fields (all nullable; additive only — existing docs unaffected)
      lastAuditedAt: data.lastAuditedAt ?? null,
      lastAuditedBy: data.lastAuditedBy ?? null,
      credentialExpiresAt,
      auditStatus: computeAuditStatus(credentialExpiresAt),
    };
    return DataService.create(CONNECTORS_COLLECTION, payload, userId, 'integration_hub');
  },

  async updateConnector(connectorId, updates, userId = 'system') {
    return DataService.update(CONNECTORS_COLLECTION, connectorId, updates, userId);
  },

  async disconnect(connectorId, userId = 'system') {
    return this.updateConnector(connectorId, {
      status: 'disconnected',
      health: 'red',
    }, userId);
  },

  /**
   * SET-006: Record an audit verification on a connector.
   * Updates lastAuditedAt, lastAuditedBy, credentialExpiresAt, and recomputes auditStatus.
   * tenantId is required on all Firestore writes.
   *
   * @param {string} connectorId
   * @param {{ credentialExpiresAt?: string|null, userId?: string, tenantId: string }} opts
   */
  async recordAudit(connectorId, { credentialExpiresAt = null, userId = 'system', tenantId }) {
    const auditStatus = computeAuditStatus(credentialExpiresAt);
    return this.updateConnector(connectorId, {
      tenantId: tid(tenantId),
      lastAuditedAt: new Date().toISOString(),
      lastAuditedBy: userId,
      credentialExpiresAt,
      auditStatus,
    }, userId);
  },

  // ── Sync logs ──────────────────────────────────────────────────────────

  async listSyncLogs(connectorId, tenantId, { limit = 25 } = {}) {
    const all = await DataService.query(SYNC_LOGS_COLLECTION, { limit }, tid(tenantId));
    return all.filter(r => !connectorId || r.connectorId === connectorId);
  },

  async recordSync({ connectorId, direction, recordsIn = 0, recordsOut = 0, errors = 0, durationMs = 0, status = 'success', summary = '' }, tenantId, userId = 'system') {
    return DataService.create(SYNC_LOGS_COLLECTION, {
      connectorId,
      tenantId: tid(tenantId),
      direction,
      recordsIn,
      recordsOut,
      errors,
      durationMs,
      status,
      summary,
      ranAt: new Date().toISOString(),
    }, userId, 'connector_sync');
  },

  // ── Field maps ─────────────────────────────────────────────────────────

  async listFieldMaps(connectorId, tenantId) {
    const all = await DataService.query(FIELD_MAPS_COLLECTION, {}, tid(tenantId));
    return all.filter(r => r.connectorId === connectorId);
  },

  async saveFieldMap({ connectorId, sourceField, kairosField, transform = null, confidence = 1, suggestedBy = 'user' }, tenantId, userId = 'system') {
    return DataService.create(FIELD_MAPS_COLLECTION, {
      connectorId,
      tenantId: tid(tenantId),
      sourceField,
      kairosField,
      transform,
      confidence,
      suggestedBy,
    }, userId, 'field_map');
  },

  // ── Health check + sync invocation ────────────────────────────────────
  // These call registered hooks when present; otherwise return a mock
  // success so every connector reports healthy in demo mode.

  async runHealthCheck(connectorId, tenantId) {
    const connector = await this.getConnector(connectorId, tenantId);
    if (!connector) return null;
    const def = registry.get(connector.connectorType);
    const start = Date.now();
    let health = 'green';
    let lastError = null;
    try {
      if (def?.hooks?.healthCheck) {
        const result = await def.hooks.healthCheck(connector);
        health = result?.health || 'green';
        lastError = result?.error || null;
      }
    } catch (e) {
      health = 'red';
      lastError = e?.message || 'Unknown error';
    }
    const durationMs = Date.now() - start;
    await this.updateConnector(connectorId, {
      health,
      lastError,
      lastHealthCheckAt: new Date().toISOString(),
      status: health === 'red' ? 'error' : 'connected',
    });
    return { connectorId, health, lastError, durationMs };
  },

  async runSync(connectorId, tenantId, { direction = 'inbound' } = {}) {
    const connector = await this.getConnector(connectorId, tenantId);
    if (!connector) return null;
    const def = registry.get(connector.connectorType);
    const start = Date.now();
    let result = { recordsIn: 0, recordsOut: 0, errors: 0, status: 'success', summary: 'Mock sync — no live credentials' };
    await this.updateConnector(connectorId, { status: 'syncing' });
    try {
      if (def?.hooks?.sync) {
        const hookResult = await def.hooks.sync(connector, { direction });
        result = { ...result, ...hookResult };
      }
    } catch (e) {
      result = { recordsIn: 0, recordsOut: 0, errors: 1, status: 'failed', summary: e?.message || 'Sync failed' };
    }
    const durationMs = Date.now() - start;
    await this.updateConnector(connectorId, {
      status: result.status === 'failed' ? 'error' : 'connected',
      lastSyncAt: new Date().toISOString(),
      lastError: result.status === 'failed' ? result.summary : null,
      health: result.status === 'failed' ? 'red' : 'green',
    });
    await this.recordSync({ connectorId, direction, durationMs, ...result }, tenantId);
    return result;
  },
};

export default IntegrationService;
